<template>
  <div id="page-tasks">
    <confirm
      id="confirmArchiveBatch"
      ref="confirmArchiveBatch"
      @confirm="archiveAll"
    >
      <template slot="title">
        <span>{{ t('tasks-confirm_archive_batch_title', { 0: tasks.length }) }}</span>
      </template>
      <template slot="body">
        <div>
          {{ t('tasks-confirm_archive_batch_body') }}
        </div>
      </template>
    </confirm>
    <confirm
      id="confirmCompleteAll"
      ref="confirmCompleteAll"
      @confirm="completeAll"
    >
      <template slot="title">
        <span>{{ t('tasks-confirm_complete_all_title', { 0: tasks.length }) }}</span>
      </template>
      <template slot="body">
        <div>
          {{ t('tasks-confirm_complete_all_body') }}
        </div>
      </template>
    </confirm>
    <confirm
      id="confirmBatchDeadline"
      ref="confirmBatchDeadline"
      @confirm="batchUpdateDeadline"
    >
      <template slot="title">
        <span>{{ t('tasks-change_deadline_batch') }}</span>
      </template>
      <template slot="body">
        <div>
          {{ t('tasks-confirm_batch_deadline_body') }}
          <div class="field mt-3">
            <label><small>{{ t('tasks-new_deadline') }}</small></label>
            <datetime-with-clear
              type="date"
              value-zone="America/Sao_Paulo"
              v-model="newDeadline"
            ></datetime-with-clear>
          </div>
        </div>
      </template>
    </confirm>
    <confirm
      id="confirmDeleteBoard"
      ref="confirmDeleteBoard"
      @confirm="deleteBoard"
    >
      <template slot="title">
        <span>
          {{ t('tasks-boards-confirm_archive_board_title_before') }}
          <b v-if="boardPendingDelete">"{{ boardPendingDelete.name }}"</b>?
        </span>
      </template>
      <template slot="body">
        <div>
          {{ t('tasks-boards-confirm_archive_board_body') }}
        </div>
      </template>
    </confirm>

    <create-task-modal
      ref="createTaskModal"
      @saved="onCreateTaskSaved"
      @close="clearCreateQuery"
    />

    <kv-modal ref="createBoardModal" size="small">
      <template #title>
        {{ t("tasks-create_board") }}
      </template>
      <template #body>
        <kv-input
          v-model="newBoardName"
          :placeholder="t('tasks-board_name_placeholder')"
          @keyup.enter="createBoard"
        />
      </template>
      <template #footer="{ toggle }">
        <kv-button variant="outlined" @click="toggle">
          {{ t("tasks-form-cancel") }}
        </kv-button>
        <kv-button
          :loading="creatingBoard"
          :disabled="creatingBoard || !newBoardName.trim()"
          @click="createBoard(toggle)"
        >
          {{ t("tasks-create_board_action") }}
        </kv-button>
      </template>
    </kv-modal>

    <div class="tasks-card">
      <div class="tasks-card-header">
        <div class="tasks-heading">
          <h1>{{ t("tasks-page_title") }}</h1>
          <span class="tasks-count" v-if="isListView && totalItems">
            {{ t("tasks-tasks_count_of_total", { 0: tasks.length, 1: totalItems }) }}
          </span>
          <span class="tasks-count" v-else-if="!isListView && currentBoardName">
            {{ currentBoardName }}
          </span>
        </div>
        <div class="tasks-card-header-actions">
          <kv-popup v-if="isListView" placement="bottom right" min-width="240px">
            <template #trigger="{ toggle }">
              <kv-button
                variant="outlined"
                size="small"
                class="tasks-view-btn"
                prepend-icon="ui-dots-grid"
                :aria-label="t('tasks-switch_to_board')"
                :title="t('tasks-switch_to_board')"
                @click="toggle"
              >
                <span class="tasks-label-full">{{ t("tasks-switch_to_board") }}</span>
              </kv-button>
            </template>
            <template #content="{ toggle }">
              <div class="tasks-boards-menu">
                <div class="tasks-boards-menu-label">
                  {{ t("tasks-select_board") }}
                </div>
                <div
                  v-for="board in boards"
                  :key="board.id"
                  class="tasks-boards-menu-item"
                  role="button"
                  tabindex="0"
                  @click="switchToBoard(board.id); toggle(false)"
                  @keyup.enter="switchToBoard(board.id); toggle(false)"
                >
                  <span class="tasks-boards-menu-name">{{ board.name }}</span>
                  <button
                    type="button"
                    class="tasks-boards-menu-delete"
                    :aria-label="t('tasks-delete_board')"
                    :title="t('tasks-delete_board')"
                    @click.stop="confirmDeleteBoard(board, toggle)"
                  >
                    <Icon icon-class="ui-trash-01" size="3" />
                  </button>
                </div>
                <div v-if="!boards.length" class="tasks-boards-menu-empty">
                  {{ t("tasks-no_boards") }}
                </div>
                <button
                  type="button"
                  class="tasks-boards-menu-create"
                  @click="openCreateBoardModal(); toggle(false)"
                >
                  <Icon icon-class="ui-plus" size="3" />
                  {{ t("tasks-create_board") }}
                </button>
              </div>
            </template>
          </kv-popup>
          <template v-else>
            <kv-button
              variant="outlined"
              size="small"
              class="tasks-view-btn"
              prepend-icon="ui-list"
              :aria-label="t('tasks-switch_to_list')"
              :title="t('tasks-switch_to_list')"
              @click="switchToList"
            >
              <span class="tasks-label-full">{{ t("tasks-switch_to_list") }}</span>
            </kv-button>
            <kv-popup placement="bottom right" min-width="240px">
              <template #trigger="{ toggle }">
                <kv-button
                  variant="outlined"
                  size="small"
                  class="tasks-board-switcher"
                  append-icon="ui-chevron-down"
                  :aria-label="t('tasks-select_board')"
                  @click="toggle"
                >
                  {{ currentBoardName || t("tasks-select_board") }}
                </kv-button>
              </template>
              <template #content="{ toggle }">
                <div class="tasks-boards-menu">
                  <div class="tasks-boards-menu-label">
                    {{ t("tasks-select_board") }}
                  </div>
                  <div
                    v-for="board in boards"
                    :key="board.id"
                    class="tasks-boards-menu-item"
                    :class="{ active: String(board.id) === String(selectedBoardId) }"
                    role="button"
                    tabindex="0"
                    @click="switchToBoard(board.id); toggle(false)"
                    @keyup.enter="switchToBoard(board.id); toggle(false)"
                  >
                    <span class="tasks-boards-menu-name">{{ board.name }}</span>
                    <button
                      type="button"
                      class="tasks-boards-menu-delete"
                      :aria-label="t('tasks-delete_board')"
                      :title="t('tasks-delete_board')"
                      @click.stop="confirmDeleteBoard(board, toggle)"
                    >
                      <Icon icon-class="ui-trash-01" size="3" />
                    </button>
                  </div>
                  <button
                    type="button"
                    class="tasks-boards-menu-create"
                    @click="openCreateBoardModal(); toggle(false)"
                  >
                    <Icon icon-class="ui-plus" size="3" />
                    {{ t("tasks-create_board") }}
                  </button>
                </div>
              </template>
            </kv-popup>
          </template>

          <kv-popup v-if="isListView" placement="bottom left" min-width="220px">
            <template #trigger="{ toggle }">
              <kv-button
                variant="outlined"
                size="small"
                class="tasks-batch-btn"
                append-icon="ui-chevron-down"
                :aria-label="t('tasks-batch_actions')"
                @click="toggle"
              >
                <span class="tasks-label-full">{{ t("tasks-batch_actions") }}</span>
                <span class="tasks-label-short">{{ t("tasks-batch_actions_short") }}</span>
              </kv-button>
            </template>
            <template #content="{ toggle }">
              <div class="tasks-batch-menu">
                <button
                  type="button"
                  class="tasks-batch-menu-item"
                  @click="$refs.confirmBatchDeadline.open(); toggle(false)"
                >
                  {{ t("tasks-change_deadline") }}
                </button>
                <button
                  type="button"
                  class="tasks-batch-menu-item"
                  @click="exportTasks(); toggle(false)"
                >
                  {{ t("tasks-export") }}
                </button>
                <button
                  v-show="hasPendingTask"
                  type="button"
                  class="tasks-batch-menu-item"
                  @click="confirmArchiveAll(); toggle(false)"
                >
                  {{ t("tasks-archive") }}
                </button>
                <button
                  v-show="hasPendingTask"
                  type="button"
                  class="tasks-batch-menu-item"
                  @click="$refs.confirmCompleteAll.open(); toggle(false)"
                >
                  {{ t("tasks-mark_as_completed") }}
                </button>
              </div>
            </template>
          </kv-popup>
          <kv-button
            variant="filled"
            size="small"
            class="tasks-add-btn"
            prepend-icon="ui-plus"
            :aria-label="t('tasks-add_new_task')"
            @click="openCreateTaskModal"
          >
            <span class="tasks-label-full">{{ t("tasks-add_new_task") }}</span>
            <span class="tasks-label-short">{{ t("tasks-add_task_short") }}</span>
          </kv-button>
        </div>
      </div>

      <div class="tasks-card-filters" v-show="filtersReady && isListView">
        <kv-filter
          v-if="filtersReady"
          ref="filterBar"
          :has-period-filter="false"
          :has-branch-filter="canFilterPerBranch"
          :has-client-filter="hasClientFilter"
          :updateQueryString="isListView"
          :filters="extraFilters"
          @changed="onFiltersChanged"
        />
      </div>

      <div class="tasks-card-body" :class="{ 'is-board-view': !isListView }">
        <template v-if="isListView">
          <div v-if="loading && tasks.length === 0" class="tasks-loading">
            <loader />
          </div>
          <template v-else-if="tasks.length > 0">
            <List
              :tasks="tasks"
              @selectTask="selectTask"
              @changed="updateTaskInfo"
              :totalTasks="totalItems"
            />
          </template>
          <div v-else class="tasks-empty-state">
            {{ t("tasks-no_tasks_found") }}
          </div>
        </template>
        <template v-else>
          <TaskBoard
            v-if="selectedBoardId"
            :key="selectedBoardId + '-' + boardInstanceKey"
            :board-id="selectedBoardId"
            :embedded="true"
          />
          <div v-else class="tasks-empty-state">
            {{ t("tasks-no_boards") }}
            <div class="mt-3">
              <kv-button
                variant="filled"
                size="small"
                prepend-icon="ui-plus"
                @click="openCreateBoardModal"
              >
                {{ t("tasks-create_board") }}
              </kv-button>
            </div>
          </div>
        </template>
      </div>
      <div class="tasks-card-footer" v-if="isListView && totalItems > 0">
        <kv-pagination
          :total="totalItems"
          :current-page="currentPage"
          :items-per-page="itemsPerPage"
          :show-items-per-page="true"
          @page-change="onPageChange"
          @items-per-page-change="onItemsPerPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import keviApi from "../keviApi";
import HumanDate from "../components/human-date/human-date";
import { mapState } from "vuex";
import BtnWhatsapp from "../components/btn-whatsapp/btn-whatsapp";
import BtnEmail from "../components/btn-email/btn-email";
import DatetimeWithClear from "../components/datetime-with-clear/datetime-with-clear.vue";
import Papa from "papaparse";
import saveData from "../helpers/saveData";
import { formatInstantInPlaceTz, placeTodayDateString } from "../helpers/datetime";
import List from "./list";
import TaskBoard from "./boards/board.vue";
import Icon from "../components/icon/icon";
import KvButton from "../components/form/kv-button.vue";
import KvPagination from "../components/ui/kv-pagination.vue";
import FilterBar from "../components/reports/kv-filter-bar.vue";
import KvPopup from "../components/ui/kv-popup.vue";
import KvModal from "../components/modal/modal.vue";
import KvInput from "../components/inputs/kv-input.vue";
import CreateTaskModal from "./create-task-modal.vue";

const VIEW_STORAGE_KEY = "tasks_last_view";
const BOARD_STORAGE_KEY = "tasks_last_board_id";

export default {
  components: {
    BtnEmail,
    BtnWhatsapp,
    HumanDate,
    "datetime-with-clear": DatetimeWithClear,
    List,
    TaskBoard,
    Icon,
    "kv-button": KvButton,
    "kv-pagination": KvPagination,
    "kv-filter": FilterBar,
    "kv-popup": KvPopup,
    "kv-modal": KvModal,
    "kv-input": KvInput,
    "create-task-modal": CreateTaskModal,
  },
  data() {
    return {
      tasks: [],
      boards: [],
      currentView: "list",
      selectedBoardId: "",
      boardPendingDelete: null,
      newBoardName: "",
      creatingBoard: false,
      activeTaskId: null,
      activeTask: null,
      noteField: "",
      savingNote: false,
      loading: false,
      filters: [],
      isMobileLayout: false,
      newDeadline: null,
      lastTaskPosition: 0,
      automations: [],
      ready: false,
      filtersReady: false,
      extraFilters: [],
      currentPage: 1,
      itemsPerPage: 50,
      totalItems: 0,
      syncingRoute: false,
      boardInstanceKey: 0,
    };
  },
  mounted() {
    (async () => {
      this.isMobileLayout = window.innerWidth <= 1024;
      window.onresize = () => {
        this.isMobileLayout = window.innerWidth <= 1024;
      };

      this.applyViewFromRouteOrStorage();
      await this.loadBoards();
      this.ensureValidBoardSelection();

      const currentQueryStringFilters = JSON.parse(
        this.$route.query.filter || "{}",
      );
      this.filters = null;
      let filters = {};
      if (Object.keys(currentQueryStringFilters).length > 0) {
        filters = currentQueryStringFilters;
      }

      await this.loadAutomations();

      if (Object.prototype.hasOwnProperty.call(this.$route.query, "id")) {
        this.activeTaskId = this.$route.query.id;
        delete this.$route.query.id;
      }

      if (this.automations.length > 0) {
        this.extraFilters.push({
          component: "checkbox",
          label: this.t("tasks-filter_automation"),
          name: "automation_id",
          icon: "ui-refresh-ccw-01",
          options: {
            options: this.automations.map((automation) => {
              return { value: automation.id, label: automation.title };
            }),
          },
          removable: true,
        });
      }

      this.extraFilters.push({
        component: "kv-select",
        label: this.t("tasks-filter_status"),
        name: "status",
        icon: "ui-check-circle",
        searchable: false,
        options: {
          options: [
            {
              value: "pending",
              label: this.t("tasks-filter_status_pending"),
            },
            {
              value: "completed",
              label: this.t("tasks-filter_status_completed"),
            },
            {
              value: "archived",
              label: this.t("tasks-filter_status_archived"),
            },
          ],
        },
        removable: false,
      });

      let level = this.$store.state.user.permissions.tasks.options.level;
      let me = null;
      if (["all", "branch"].includes(level)) {
        await this.$store.dispatch("placeDetails/setPlace", this.placeId);
        let users = this.users.filter((u) => parseInt(u.active) === 1);
        me = users.find((u) => u.username === this.user.username);
        if (level === "branch") {
          users = users.filter(
            (u) => u.places_branches_id === me.places_branches_id,
          );
        }
        this.extraFilters.push({
          component: "kv-select",
          label: this.t("tasks-filter_assignee"),
          name: "assignee",
          icon: "ui-user-square",
          searchable: true,
          options: {
            options: users.map((u) => ({ value: u.id, label: u.name })),
          },
          removable: true,
        });
      }

      this.extraFilters.push({
        component: "period",
        label: this.t("tasks-filter_task_due_date"),
        name: "task-due-date-period",
        icon: "ui-calendar-check-02",
        removable: true,
      });
      this.extraFilters.push({
        component: "period",
        label: this.t("tasks-filter_creation_date"),
        name: "task-created-date-period",
        icon: "ui-calendar-date",
        removable: true,
      });

      await this.loadTagFilter();

      this.filtersReady = true;

      await this.$nextTick();
      const filterBar = this.$refs.filterBar;
      if (filterBar) {
        if (Object.keys(filters).length > 0) {
          filterBar.setValues(filters);
        }
        filterBar.addFilter("status", false);
        if (!["pending", "completed", "archived"].includes(filters?.status)) {
          filterBar.setValue("status", "pending");
        }
      }

      if (this.$route.query.task_id) {
        this.activeTaskId = this.$route.query.task_id;
      }

      this.ready = true;
      this.persistViewState();

      if (this.$route.query.create === "1" || this.$route.query.create === 1) {
        await this.$nextTick();
        this.openCreateTaskModal();
        this.clearCreateQuery();
      }
    })();
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
    ...mapState("placeDetails", ["users", "roles", "branches"]),
    placeId() {
      return this.$store.state.place.id;
    },
    isListView() {
      return this.currentView !== "board";
    },
    currentBoardName() {
      const board = this.boards.find(
        (b) => String(b.id) === String(this.selectedBoardId),
      );
      return board ? board.name : "";
    },
    canFilterPerBranch() {
      let inbox = this.user.permissions.inbox;
      return (
        parseInt(inbox.enabled) === 1 &&
        parseInt(inbox.options.global_access) === 1
      );
    },
    hasBranchFilter() {
      return true;
    },
    hasClientFilter() {
      return true;
    },
    hasPendingTask() {
      return (
        this.tasks.filter((t) => t.archived === "0" && !t.completed_at).length >
        0
      );
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },

    applyViewFromRouteOrStorage() {
      const queryView = this.$route.query.view;
      if (queryView === "board" || queryView === "list") {
        this.currentView = queryView;
      } else {
        const savedView = localStorage.getItem(VIEW_STORAGE_KEY);
        this.currentView =
          savedView === "board" || savedView === "list" ? savedView : "list";
      }

      if (this.$route.query.board_id) {
        this.selectedBoardId = String(this.$route.query.board_id);
      } else {
        const savedBoardId = localStorage.getItem(BOARD_STORAGE_KEY);
        this.selectedBoardId = savedBoardId ? String(savedBoardId) : "";
      }
    },

    persistViewState() {
      localStorage.setItem(VIEW_STORAGE_KEY, this.currentView);
      if (this.selectedBoardId) {
        localStorage.setItem(BOARD_STORAGE_KEY, String(this.selectedBoardId));
      }

      const query = { ...this.$route.query };
      query.view = this.currentView;
      if (this.currentView === "board" && this.selectedBoardId) {
        query.board_id = String(this.selectedBoardId);
      } else {
        delete query.board_id;
      }

      const current = this.$route.query;
      if (
        current.view === query.view &&
        String(current.board_id || "") === String(query.board_id || "")
      ) {
        return;
      }

      this.syncingRoute = true;
      this.$router
        .replace({ query })
        .catch(() => {})
        .finally(() => {
          this.syncingRoute = false;
        });
    },

    ensureValidBoardSelection() {
      if (!this.boards.length) {
        if (this.currentView === "board") {
          this.currentView = "list";
        }
        this.selectedBoardId = "";
        return;
      }

      const exists = this.boards.some(
        (b) => String(b.id) === String(this.selectedBoardId),
      );
      if (!exists) {
        this.selectedBoardId = String(this.boards[0].id);
      }
    },

    switchToList() {
      this.currentView = "list";
      this.persistViewState();
      if (this.ready) {
        this.loadTasks();
      }
    },

    switchToBoard(boardId) {
      if (boardId) {
        this.selectedBoardId = String(boardId);
      } else if (!this.selectedBoardId && this.boards.length) {
        this.selectedBoardId = String(this.boards[0].id);
      }
      if (!this.selectedBoardId) {
        this.openCreateBoardModal();
        return;
      }
      this.currentView = "board";
      this.persistViewState();
    },

    openCreateBoardModal() {
      this.newBoardName = "";
      this.$refs.createBoardModal.open();
    },

    async loadBoards() {
      try {
        const options = keviApi.getOptions("/task-boards", "GET");
        const response = await this.$http(options);
        this.boards = response.body || response.data || [];
      } catch (error) {
        console.error("Error loading boards:", error);
        this.boards = [];
      }
    },

    async createBoard(toggle) {
      const name = (this.newBoardName || "").trim();
      if (!name || this.creatingBoard) {
        return;
      }
      this.creatingBoard = true;
      try {
        const options = keviApi.getOptions("/task-boards", "POST", { name });
        const response = await this.$http(options);
        const board = response.body || response.data;
        if (board) {
          this.boards.push(board);
          this.$store.dispatch(
            "notification/success",
            this.t("tasks-boards-notify_board_created"),
          );
          if (typeof toggle === "function") {
            toggle();
          }
          if (this.$refs.createBoardModal) {
            this.$refs.createBoardModal.close();
          }
          this.newBoardName = "";
          this.switchToBoard(board.id);
        }
      } catch (error) {
        console.error("Error creating board:", error);
        this.$store.dispatch(
          "notification/error",
          this.t("tasks-notify_error_load"),
        );
      } finally {
        this.creatingBoard = false;
      }
    },

    confirmDeleteBoard(board, toggle) {
      this.boardPendingDelete = board;
      if (typeof toggle === "function") {
        toggle(false);
      }
      this.$nextTick(() => {
        this.$refs.confirmDeleteBoard.open();
      });
    },

    async deleteBoard() {
      const board = this.boardPendingDelete;
      if (!board) {
        return;
      }
      try {
        const options = keviApi.getOptions(
          "/task-boards/" + board.id,
          "DELETE",
        );
        await this.$http(options);
        this.boards = this.boards.filter(
          (b) => String(b.id) !== String(board.id),
        );
        this.$store.dispatch(
          "notification/success",
          this.t("tasks-boards-notify_board_archived"),
        );

        if (String(this.selectedBoardId) === String(board.id)) {
          if (this.boards.length) {
            this.selectedBoardId = String(this.boards[0].id);
            this.currentView = "board";
          } else {
            this.selectedBoardId = "";
            this.currentView = "list";
          }
        }
        this.persistViewState();
      } catch (error) {
        console.error("Error deleting board:", error);
        this.$store.dispatch(
          "notification/error",
          this.t("tasks-notify_error_load"),
        );
      } finally {
        this.boardPendingDelete = null;
      }
    },

    openCreateTaskModal() {
      if (!this.$refs.createTaskModal) {
        return;
      }
      const defaults = {};
      if (this.currentView === "board" && this.selectedBoardId) {
        defaults.tasks_boards_id = this.selectedBoardId;
      }
      this.$refs.createTaskModal.open(defaults);
    },

    onCreateTaskSaved(task) {
      this.clearCreateQuery();
      if (this.isListView) {
        this.refreshTasks();
        if (task && task.id) {
          this.activeTaskId = task.id;
        }
      } else {
        this.boardInstanceKey += 1;
      }
    },

    clearCreateQuery() {
      if (
        this.$route.query.create === undefined ||
        this.$route.query.create === null
      ) {
        return;
      }
      const query = { ...this.$route.query };
      delete query.create;
      this.$router.replace({ query }).catch(() => {});
    },

    onFiltersChanged(filters) {
      this.resetPagination();
      this.loadTasks(filters);
    },

    onPageChange(page) {
      if (page === this.currentPage) {
        return;
      }
      this.currentPage = page;
      this.loadTasks();
    },

    onItemsPerPageChange(perPage) {
      const next = Number(perPage) || 50;
      if (next === this.itemsPerPage) {
        return;
      }
      this.itemsPerPage = next;
      this.currentPage = 1;
      this.loadTasks();
    },

    resetPagination() {
      this.currentPage = 1;
      this.tasks = [];
      this.totalItems = 0;
    },

    selectTask(taskId) {
      if (taskId === null) {
        this.activeTaskId = null;
        this.activeTask = null;
        this.$router.push("/tasks");
      } else {
        this.activeTaskId = taskId;
      }
    },

    isLateClass(task) {
      if (!task.completed_at && task.due_date) {
        const today = placeTodayDateString(this.$store.state.place.timezone);
        const dueDay = String(task.due_date).slice(0, 10);
        if (dueDay < today) {
          return "late";
        }
        if (dueDay === today) {
          return "text-warning";
        }
      }
      return "";
    },

    async loadAutomations() {
      const options = keviApi.getOptions("tasks/automations", "GET");
      this.automations = (await this.$http(options)).body;
    },

    async loadTagFilter() {
      try {
        const response = await this.$http(keviApi.getOptions("tasks-tags"));
        const tags = (response.body && response.body.tags) || [];
        if (!tags.length) {
          return;
        }
        this.extraFilters.push({
          component: "checkbox",
          name: "tags",
          label: this.t("tasks-filter_tags"),
          options: {
            options: tags.map((tag) => ({
              value: tag.id,
              label: tag.name,
              color: tag.color,
            })),
            icon: "ui-tag-01",
          },
          removable: true,
        });
      } catch (error) {
        console.error("Error loading task tags filter", error);
      }
    },

    async loadTasks(filters) {
      if (!this.isListView) {
        return;
      }
      if (!filters) {
        filters = this.filters;
      } else {
        this.filters = Object.assign({}, filters);
      }
      if (!this.ready) {
        return;
      }

      this.loading = true;

      try {
        const options = keviApi.getOptions("tasks", "GET", {
          filters,
          page: this.currentPage,
          per_page: this.itemsPerPage
        });

        const response = await this.$http(options);
        this.tasks = response.body.data || response.body || [];

        if (response.body.meta) {
          this.totalItems = response.body.meta.total || 0;
          const lastPage = Number(response.body.meta.last_page) || 1;
          if (this.currentPage > lastPage) {
            this.currentPage = lastPage;
            if (lastPage >= 1) {
              await this.loadTasks(filters);
              return;
            }
          }
        } else {
          this.totalItems = this.tasks.length;
        }
      } catch (error) {
        console.error("Error loading tasks:", error);
        this.$store.dispatch("notification/error", this.t('tasks-notify_error_load'));
      } finally {
        this.loading = false;
      }
    },

    loadActiveTask() {
      if (this.activeTaskId) {
        const options = keviApi.getOptions("task/" + this.activeTaskId, "GET");
        this.$http(options).then((response) => {
          this.activeTask = response.body.task;
        });
      } else {
        this.activeTask = null;
      }
    },

    complete(id) {
      const options = keviApi.getOptions(`task/${id}/complete`, "POST");
      this.$http(options).then((response) => {
        this.$store.dispatch("notification/success", this.t('tasks-notify_task_completed'));
        window.mixpanel?.track("Task completed", this.activeTask);
        this.updateActiveTaskOnList(id, {
          completed_at: response.body.task.completed_at,
        });
      });
    },

    archive(id) {
      let task = this.tasks.find((t) => t.id == id);
      const options = keviApi.getOptions(`task/${id}/archive`, "POST", {
        archived: task.archived == 0 ? true : false,
      });
      this.$http(options).then((response) => {
        let task = response.body.task;
        this.$store.dispatch(
          "notification/success",
          task.archived ? this.t('tasks-notify_task_archived') : this.t('tasks-notify_task_unarchived'),
        );
        window.mixpanel?.track(
          task.archived ? "Task archived" : "Task unarchived",
          task,
        );
        this.loadActiveTask();
        this.updateActiveTaskOnList(id, {
          archived: task.archived,
        });
      });
    },

    archiveAll() {
      let ids = this.tasks.map((t) => t.id);
      const options = keviApi.getOptions("task/archiveBatch", "POST", {
        ids,
      });
      this.$http(options).then((response) => {
        if (response.status === 200) {
          this.$store.dispatch(
            "notification/success",
            this.t('tasks-notify_tasks_archived', { 0: ids.length }),
          );
          window.mixpanel?.track("Batch task archived");
          this.resetPagination();
          this.loadTasks(this.filters);
          this.loadActiveTask();
        }
      });
    },

    completeAll() {
      let ids = this.tasks.map((t) => t.id);
      const options = keviApi.getOptions("task/completeBatch", "POST", {
        ids,
      });
      this.$http(options).then((response) => {
        if (response.status === 200) {
          this.$store.dispatch(
            "notification/success",
            this.t('tasks-notify_tasks_completed', { 0: ids.length }),
          );
          window.mixpanel?.track("Batch task completed");
          this.resetPagination();
          this.loadTasks(this.filters);
          this.loadActiveTask();
        }
      });
    },

    batchUpdateDeadline() {
      let ids = this.tasks.map((t) => t.id);
      const options = keviApi.getOptions("task/batchUpdateDeadline", "POST", {
        ids,
        deadline: this.newDeadline,
      });
      this.$http(options).then((response) => {
        if (response.status === 200) {
          this.$store.dispatch(
            "notification/success",
            this.t('tasks-notify_deadlines_updated', { 0: ids.length }),
          );
          window.mixpanel?.track("Batch deadline updated");
          this.resetPagination();
          this.loadTasks(this.filters);
          this.newDeadline = null;
          this.loadActiveTask();
        }
      });
    },

    exportTasks() {
      const tz = this.$store.state.place.timezone;
      const locale = this.$store.state.place.locale;
      const tasks = this.tasks.map((task) => {
        const clientName =
          task.client_name ||
          [task.client?.first_name, task.client?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
        return {
          Título: task.title,
          Responsável: task.assignee_name,
          Prioridade: task.priority,
          Cliente: clientName,
          "Id Cliente": task.clients_id,
          Telefone: task.client?.whatsapp || task.whatsapp,
          "E-mail": task.client?.email || task.email,
          Vencimento: task.due_date,
          Quadro: task.board_name,
          Etapa: task.column_name,
          Descrição: task.description,
          "Mensagem sugerida": task.suggested_message,
          "Data conclusão": formatInstantInPlaceTz(task.completed_at, tz, locale),
          Arquivada: task.archived === "1" || task.archived === 1 ? "Sim" : "Não",
        };
      });
      const csv = Papa.unparse(tasks);
      saveData(csv, "tasks.csv");
    },

    confirmArchiveAll() {
      this.$refs.confirmArchiveBatch.open();
    },

    refreshTasks() {
      this.resetPagination();
      this.loadTasks(this.filters);
      this.loadActiveTask();
    },

    addNote() {
      this.savingNote = true;
      let options = keviApi.getOptions(
        "task/" + this.activeTask.id + "/notes",
        "PUT",
        {
          note: this.noteField,
        },
      );
      return this.$http(options).then((response) => {
        this.savingNote = false;
        this.noteField = "";
        this.activeTask.notes.push(response.body);
        this.$store.dispatch(
          "notification/success",
          this.t('tasks-notify_note_saved'),
        );
      });
    },

    updateTaskInfo({ id, task, moveNext }) {
      if (id && task) {
        this.tasks = this.tasks.map((t) => {
          if (String(t.id) === String(id)) {
            return { ...t, ...task };
          }
          return t;
        });
        if (String(this.activeTaskId) === String(id)) {
          this.activeTask = { ...this.activeTask, ...task };
        }
        if (moveNext) {
          const nextIndex =
            this.tasks.findIndex((t) => String(t.id) === String(id)) + 1;
          if (nextIndex < this.tasks.length) {
            this.activeTaskId = this.tasks[nextIndex].id;
          }
        }
      }
    },

    updateActiveTaskOnList(id, patch) {
      this.tasks = this.tasks.map((t) => {
        if (String(t.id) === String(id)) {
          return { ...t, ...patch };
        }
        return t;
      });
    },
  },
  watch: {
    ready() {
      if (this.ready && this.isListView) {
        this.loadTasks();
      }
    },
    activeTaskId() {
      if (this.activeTaskId) {
        let newQuery = JSON.parse(JSON.stringify(this.$route.query));
        if (newQuery["task_id"] !== this.activeTaskId) {
          newQuery["task_id"] = this.activeTaskId;
          this.$router.replace({ query: newQuery });
          const lastIndex = this.tasks.findIndex(
            (t) => t.id === this.activeTaskId,
          );
          this.lastTaskPosition = lastIndex >= 0 ? lastIndex : 0;
        }
        this.loadActiveTask();
      }
    },
    "$route.query": {
      deep: true,
      handler(query) {
        if (this.syncingRoute) {
          return;
        }
        if (query.create === "1" || query.create === 1) {
          this.openCreateTaskModal();
          this.clearCreateQuery();
        }
        const nextView =
          query.view === "board" || query.view === "list"
            ? query.view
            : null;
        if (nextView && nextView !== this.currentView) {
          this.currentView = nextView;
        }
        if (
          query.board_id &&
          String(query.board_id) !== String(this.selectedBoardId)
        ) {
          this.selectedBoardId = String(query.board_id);
        }
        if (this.currentView === "board") {
          this.ensureValidBoardSelection();
        }
        localStorage.setItem(VIEW_STORAGE_KEY, this.currentView);
        if (this.selectedBoardId) {
          localStorage.setItem(
            BOARD_STORAGE_KEY,
            String(this.selectedBoardId),
          );
        }
        if (this.ready && this.isListView) {
          this.loadTasks();
        }
      },
    },
  },
};
</script>

<style scoped>
.tasks-batch-menu {
  display: flex;
  flex-direction: column;
  padding: 6px;
  gap: 2px;
}

.tasks-batch-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  color: var(--text-primary, #101828);
  font-size: 14px;
  line-height: 1.3;
  cursor: pointer;
}

.tasks-batch-menu-item:hover {
  background: var(--bg-secondary, #f2f4f7);
}

.tasks-batch-btn ::v-deep .kv-button-content,
.tasks-board-switcher ::v-deep .kv-button-content,
.tasks-view-btn ::v-deep .kv-button-content {
  align-items: center;
  gap: 6px;
}

.tasks-boards-menu {
  display: flex;
  flex-direction: column;
  padding: 8px;
  gap: 2px;
}

.tasks-boards-menu-label {
  padding: 6px 10px 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary, #667085);
}

.tasks-boards-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--text-primary, #101828);
  font-size: 14px;
  line-height: 1.3;
  cursor: pointer;
}

.tasks-boards-menu-item:hover,
.tasks-boards-menu-item.active {
  background: var(--bg-secondary, #f2f4f7);
}

.tasks-boards-menu-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tasks-boards-menu-delete {
  border: 0;
  background: transparent;
  color: #98a2b3;
  padding: 2px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tasks-boards-menu-delete:hover {
  color: #b42318;
  background: #fef3f2;
}

.tasks-boards-menu-empty {
  padding: 10px;
  font-size: 13px;
  color: #667085;
}

.tasks-boards-menu-create {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
  border: 1px dashed #d0d5dd;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  color: var(--text-primary, #101828);
  font-size: 14px;
  cursor: pointer;
}

.tasks-boards-menu-create:hover {
  border-color: var(--primary, #0b9055);
  color: var(--primary, #0b9055);
  background: #edfcf3;
}

.tasks-card-body.is-board-view {
  padding-top: 0;
}

.scrolling-component {
  /* Garantir que o scroll funcione corretamente */
  min-height: 100vh;
}

.loading-more {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  color: #666;
}

.no-more-items {
  text-align: center;
  padding: 20px;
  color: #999;
  font-style: italic;
}

.no-more-items p {
  margin: 0;
}
</style>
